import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Avatar,
  Button,
  TextField,
  Grid,
  CircularProgress,
  Snackbar,
  Alert,
  Paper,
  IconButton,
  InputAdornment,
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  CameraAlt as CameraAltIcon,
  Save as SaveIcon,
  Lock as LockIcon,
  AccountCircle as AccountCircleIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import AppService from '../../../Configurations/Appservice';
import { API_BASE_URL } from '../../../utils/constants';

// Theme color matching sidebar
const THEME_COLOR = '#1a8a7e';
const THEME_COLOR_DARK = '#157a6f';
const THEME_GRADIENT = 'linear-gradient(135deg, #1a8a7e 0%, #2aa89a 100%)';

// Helper: get auth token from localStorage without hardcoding
const getAuthToken = () => {
  try {
    const authInfo = localStorage.getItem('authInfo');
    if (authInfo) {
      const obj = JSON.parse(authInfo);
      if (obj && obj.token) return obj.token;
    }
  } catch {}
  return localStorage.getItem('token') || localStorage.getItem('jwt_token');
};

const authHeaders = (extra = {}) => ({
  Authorization: `Bearer ${getAuthToken()}`,
  ...extra,
});

const AdminProfile = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [profile, setProfile] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    organizationName: '',
    organizationCode: '',
    profilePhotoPath: '',
  });
  const [avatarVersion, setAvatarVersion] = useState(Date.now());
  const [avatarSrc, setAvatarSrc] = useState('');
  const fileRef = useRef(null);

  const fetchAvatar = async (filePath) => {
    try {
      if (!filePath) {
        setAvatarSrc('');
        return;
      }
      // Important: do NOT include cache-busting in filePath value itself
      // Add version as a separate query parameter to avoid S3 key mismatches
      const url = `${API_BASE_URL}/v1/files/download/bytes?filePath=${encodeURIComponent(filePath)}&v=${avatarVersion}`;
      const res = await fetch(url, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Avatar fetch failed (${res.status})`);
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      setAvatarSrc((prev) => {
        if (prev && prev.startsWith('blob:')) URL.revokeObjectURL(prev);
        return objectUrl;
      });
    } catch (e) {
      // keep silent error to avoid noisy UI; avatar just not shown
      setAvatarSrc('');
      console.error('Avatar load error:', e);
    }
  };

  const loadProfile = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await AppService.get('v1/users/me');
      setProfile({
        name: data?.name || '',
        email: data?.email || data?.emailId || '',
        phoneNumber: data?.phoneNumber || data?.phone || '',
        organizationName: data?.organizationName || data?.organisationName || '',
        organizationCode: data?.organizationCode || data?.organisationCode || '',
        profilePhotoPath: data?.profilePhotoPath || data?.profileUrl || '',
      });
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProfile();
  }, []);

  // Load avatar whenever profilePhotoPath or version changes
  useEffect(() => {
    if (profile.profilePhotoPath) {
      fetchAvatar(profile.profilePhotoPath);
    } else {
      // cleanup old blob if any
      setAvatarSrc((prev) => {
        if (prev && prev.startsWith('blob:')) URL.revokeObjectURL(prev);
        return '';
      });
    }
    // Cleanup on unmount
    return () => {
      setAvatarSrc((prev) => {
        if (prev && prev.startsWith('blob:')) URL.revokeObjectURL(prev);
        return '';
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile.profilePhotoPath, avatarVersion]);

  const onSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const payload = {
        name: profile.name,
        email: profile.email,
        phoneNumber: profile.phoneNumber,
      };
      const data = await AppService.put('v1/users/me', payload);
      setProfile((prev) => ({
        ...prev,
        name: data?.name ?? prev.name,
        email: data?.email ?? data?.emailId ?? prev.email,
        phoneNumber: data?.phoneNumber ?? prev.phoneNumber,
      }));
      setSuccess('Profile updated');
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const onUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    setError('');
    setSuccess('');
    try {
      const form = new FormData();
      form.append('file', file);
      const data = await AppService.post('v1/users/me/photo', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const newPath = data?.profilePhotoPath || profile.profilePhotoPath;
      setProfile((p) => ({ ...p, profilePhotoPath: newPath }));
      setAvatarVersion(Date.now());
      // Persist for other app parts (e.g., sidebar) to refresh avatar
      try {
        if (newPath) {
          localStorage.setItem('profilePhotoPath', newPath);
          localStorage.setItem('profilePhotoUpdatedAt', String(Date.now()));
        }
      } catch {}
      setSuccess('Profile photo updated');
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Failed to upload photo');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  // avatarSrc is managed via authorized fetch above

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, height: '100%' }}>
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
          <CircularProgress size={48} sx={{ color: THEME_COLOR }} />
        </Box>
      ) : (
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border: '1px solid #e0e0e0',
            overflow: 'hidden',
            height: '100%',
            minHeight: 500,
          }}
        >
          <Grid container sx={{ height: '100%' }}>
            {/* Left Side - Profile Branding */}
            <Grid
              item
              xs={12}
              md={5}
              sx={{
                background: THEME_GRADIENT,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                p: 4,
                color: '#fff',
                minHeight: { xs: 280, md: 'auto' },
              }}
            >
              {/* Avatar with upload */}
              <Box sx={{ position: 'relative', mb: 3 }}>
                <Avatar
                  src={avatarSrc}
                  alt={profile.name}
                  sx={{
                    width: 120,
                    height: 120,
                    border: '4px solid rgba(255,255,255,0.3)',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
                    fontSize: '2.5rem',
                    bgcolor: 'rgba(255,255,255,0.2)',
                  }}
                >
                  {!avatarSrc && profile.name?.charAt(0)?.toUpperCase()}
                </Avatar>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/jpeg,image/png"
                  style={{ display: 'none' }}
                  onChange={(e) => onUpload(e.target.files?.[0])}
                />
                <IconButton
                  onClick={() => fileRef.current && fileRef.current.click()}
                  disabled={uploading}
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    right: 0,
                    bgcolor: 'rgba(255,255,255,0.9)',
                    color: THEME_COLOR,
                    width: 36,
                    height: 36,
                    '&:hover': { bgcolor: '#fff' },
                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                  }}
                >
                  {uploading ? <CircularProgress size={18} sx={{ color: THEME_COLOR }} /> : <CameraAltIcon fontSize="small" />}
                </IconButton>
              </Box>

              <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5, textAlign: 'center' }}>
                My Profile
              </Typography>
              <Typography variant="body1" sx={{ textAlign: 'center', opacity: 0.9, maxWidth: 280, mb: 3 }}>
                Manage your personal information and account settings
              </Typography>

              {/* Organization Info */}
              <Box sx={{ mt: 2, width: '100%', maxWidth: 280 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                  <CheckCircleIcon sx={{ fontSize: 18, opacity: 0.9 }} />
                  <Box>
                    <Typography variant="caption" sx={{ opacity: 0.8, display: 'block' }}>
                      Organization
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {profile.organizationName || '-'}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <CheckCircleIcon sx={{ fontSize: 18, opacity: 0.9 }} />
                  <Box>
                    <Typography variant="caption" sx={{ opacity: 0.8, display: 'block' }}>
                      Organization Code
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {profile.organizationCode || '-'}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>

            {/* Right Side - Edit Form */}
            <Grid
              item
              xs={12}
              md={7}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                p: { xs: 3, md: 5 },
              }}
            >
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 1, color: '#1a1a2e' }}>
                Personal Information
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                Update your profile details below
              </Typography>

              {/* Form Fields */}
              <Box sx={{ mb: 2.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#424242' }}>
                  Full Name
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={profile.name}
                  onChange={(e) => setProfile((p) => ({ ...p, name: e.target.value }))}
                  placeholder="Enter your full name"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon sx={{ color: '#9e9e9e', fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Box>

              <Box sx={{ mb: 2.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#424242' }}>
                  Email Address
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  type="email"
                  value={profile.email}
                  onChange={(e) => setProfile((p) => ({ ...p, email: e.target.value }))}
                  placeholder="Enter your email"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon sx={{ color: '#9e9e9e', fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#424242' }}>
                  Phone Number
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={profile.phoneNumber}
                  onChange={(e) => setProfile((p) => ({ ...p, phoneNumber: e.target.value }))}
                  placeholder="+971 5X XXX XXXX"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneIcon sx={{ color: '#9e9e9e', fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Box>

              {/* Action Buttons */}
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mt: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<LockIcon />}
                  onClick={() => navigate('/ADM-APPCONFIG/reset_password')}
                  sx={{
                    borderRadius: 2,
                    px: 3,
                    py: 1.2,
                    textTransform: 'none',
                    fontWeight: 600,
                    borderColor: '#e0e0e0',
                    color: '#424242',
                    '&:hover': {
                      borderColor: '#bdbdbd',
                      bgcolor: '#fafafa',
                    },
                  }}
                >
                  Change Password
                </Button>
                <Button
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                  onClick={onSave}
                  disabled={saving}
                  sx={{
                    borderRadius: 2,
                    px: 4,
                    py: 1.2,
                    textTransform: 'none',
                    fontWeight: 600,
                    bgcolor: THEME_COLOR,
                    color: '#ffffff',
                    boxShadow: `0 4px 14px rgba(26,138,126,0.4)`,
                    '&:hover': {
                      bgcolor: THEME_COLOR_DARK,
                    },
                  }}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Snackbars */}
      <Snackbar
        open={!!error}
        autoHideDuration={4000}
        onClose={() => setError('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity="error" onClose={() => setError('')} sx={{ borderRadius: 2 }}>
          {error}
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!success}
        autoHideDuration={3000}
        onClose={() => setSuccess('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity="success" onClose={() => setSuccess('')} sx={{ borderRadius: 2 }}>
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminProfile;
